import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

const HebrewArc = () => {
  // Reverse the string so LTR textPath renders it correctly for RTL reading
  const original = "קול ששון וקול שמחה, קול חתן וקול כלה";
  const reversed = [...original].reverse().join("");

  return (
    <svg viewBox="0 0 600 80" className="w-[320px] md:w-[500px] mx-auto" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <path id="arc" d="M 40,70 Q 300,-5 560,70" fill="none" />
      </defs>
      <text fill="#C5A059" fontSize="25" fontFamily="Frank Ruhl Libre, David, serif" letterSpacing="1" direction="ltr" style={{ unicodeBidi: "bidi-override" }}>
        <textPath href="#arc" startOffset="50%" textAnchor="middle">
          {reversed}
        </textPath>
      </text>
    </svg>
  );
};

const HeroSection = () => {
  const { t, i18n } = useTranslation();
  const isHe = i18n.language === "he";

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
      opacity: 1, y: 0,
      transition: { delay: i * 0.15, duration: 0.8, ease: [0.22, 1, 0.36, 1] }
    })
  };

  return (
    <section data-testid="hero-section" className="relative min-h-[100svh] flex flex-col items-center bg-cream overflow-hidden px-6 pt-4 gap-4 md:justify-center md:py-0 md:gap-0">
      {/* BS"D */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.8 }}
        className="absolute top-6 right-6 font-serif text-gold text-lg tracking-widest z-50"
      >
        {t("bsd")}
      </motion.p>

      {/* Top group: line + monogram + blessing */}
      <motion.div
        className="flex flex-col items-center text-center md:mb-8"
        initial="hidden"
        animate="visible"
      >
        {/* Decorative line */}
        <div className="w-px h-14 md:h-16 bg-gradient-to-b from-transparent via-gold to-transparent mb-2 md:mb-6" />

        {/* Monogram */}
        <motion.div custom={0} variants={fadeUp} className="mb-3 md:mb-5">
          <svg viewBox="0 0 120 120" className="w-16 h-16 md:w-24 md:h-24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="60" cy="60" r="58" stroke="#C5A059" strokeWidth="0.5"/>
            <circle cx="60" cy="60" r="52" stroke="#C5A059" strokeWidth="0.3"/>
            <text x="60" y="40" dominantBaseline="middle" textAnchor="middle" fill="#C5A059" fontFamily="Frank Ruhl Libre, serif" fontSize="26" fontWeight="300">N</text>
            <line x1="38" y1="60" x2="82" y2="60" stroke="#C5A059" strokeWidth="0.4"/>
            <text x="60" y="84" dominantBaseline="middle" textAnchor="middle" fill="#C5A059" fontFamily="Frank Ruhl Libre, serif" fontSize="26" fontWeight="300">A</text>
          </svg>
        </motion.div>

        {/* Hebrew blessing - arc */}
        <motion.div custom={1} variants={fadeUp}>
          <HebrewArc />
        </motion.div>
      </motion.div>

      {/* Center group: names + subtitle */}
      <motion.div
        className="flex flex-col items-center text-center md:mt-0"
        initial="hidden"
        animate="visible"
      >
        <motion.div custom={2} variants={fadeUp}>
          <h1 className="font-serif text-charcoal text-4xl md:text-7xl lg:text-8xl tracking-tight leading-tight">
            {t("groom")}
          </h1>
        </motion.div>

        <motion.div custom={3} variants={fadeUp}>
          <span className="font-serif text-gold text-2xl md:text-4xl italic my-1 md:my-2 inline-block">
            {t("and")}
          </span>
        </motion.div>

        <motion.div custom={4} variants={fadeUp}>
          <h1 className="font-serif text-charcoal text-4xl md:text-7xl lg:text-8xl tracking-tight leading-tight">
            {t("bride")}
          </h1>
        </motion.div>

        <motion.p
          custom={5}
          variants={fadeUp}
          className="font-sans text-secondary text-xs md:text-base tracking-[0.2em] md:tracking-[0.25em] uppercase mt-6 md:mt-10"
        >
          {t("hero_subtitle")}
        </motion.p>
      </motion.div>

      {/* Spacer - mobile only */}
      <div className="flex-1 md:hidden" />

      {/* Date group */}
      <motion.div
        className="flex flex-col items-center text-center md:mt-8"
        initial="hidden"
        animate="visible"
      >
        <motion.div custom={6} variants={fadeUp}>
          <div className="w-12 md:w-16 h-px bg-gold mx-auto mb-3 md:mb-4" />
          <p className="font-sans text-secondary text-xs md:text-base tracking-[0.15em] md:tracking-[0.2em] uppercase">
            {t("wedding_date")}
          </p>
          <p className="font-serif text-gold text-xs md:text-base mt-1.5 md:mt-2" dir="rtl">
            {t("wedding_date_hebrew")}
          </p>
          <p className="font-serif text-secondary/60 text-xs md:text-sm mt-2 md:mt-3 italic">
            {t("arrival_time")}
          </p>
          <div className="w-12 md:w-16 h-px bg-gold mx-auto mt-3 md:mt-4" />
        </motion.div>
      </motion.div>

      {/* Spacer - mobile only */}
      <div className="flex-1 md:hidden" />

      {/* Memorial phrase - stays at bottom */}
      <motion.p
        custom={7}
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        className="pb-10 md:mt-4 md:pb-0 font-sans text-secondary/70 text-[10px] md:text-sm max-w-md mx-auto leading-relaxed italic text-center"
      >
        {t("memorial_phrase")}
      </motion.p>
    </section>
  );
};

export default HeroSection;

